/**
 * PostureCapture — MediaPipe Pose live camera overlay. Iter 75.
 *
 * Loads MediaPipe Tasks (Pose Landmarker) on demand via CDN, asks for camera
 * permission, runs landmark detection at ~15 fps, and overlays joint angles
 * + simple posture-class warnings on top of the live video.
 *
 * Why CDN instead of `yarn add`:
 *   • MediaPipe ships a 12 MB+ WASM bundle + model. We don't want to bloat
 *     the main bundle for clinics that don't use this feature.
 *   • Lazy-loading from the CDN keeps the rest of the app's cold-start under
 *     1.5 MB and only fetches the model when a clinician actually clicks
 *     "Start posture capture".
 *
 * Mounted at /admin/posture-capture (clinician-only via StaffRoutes).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Camera, Loader2, AlertCircle, Activity, CheckCircle2, XCircle, Download, Save, User, Image as ImageIcon, FileDown, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';

const MEDIAPIPE_VISION_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/vision_bundle.mjs';
const POSE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

// Pose landmark indices (subset we care about for posture analysis).
const LM = {
  NOSE: 0, L_SHOULDER: 11, R_SHOULDER: 12, L_ELBOW: 13, R_ELBOW: 14,
  L_HIP: 23, R_HIP: 24, L_KNEE: 25, R_KNEE: 26,
};

// Each row: [name, A, B, C] where the angle is measured at B (BA · BC).
const ANGLES = [
  ['Left elbow',  LM.L_SHOULDER, LM.L_ELBOW, LM.NOSE],
  ['Right elbow', LM.R_SHOULDER, LM.R_ELBOW, LM.NOSE],
  ['Trunk lean',  LM.L_SHOULDER, LM.L_HIP, LM.L_KNEE],
];

const BODY_PARTS = [
  'Full body', 'Head & neck', 'Shoulders', 'Upper back (thoracic)',
  'Lower back (lumbar)', 'Pelvis & hips', 'Knees', 'Ankles & feet',
];

const VIEWS = [
  { key: 'anterior',  label: 'Anterior',         hint: 'Face the camera, arms by sides, feet shoulder-width.' },
  { key: 'posterior', label: 'Posterior',        hint: 'Back to camera, arms by sides, feet shoulder-width.' },
  { key: 'lateral_l', label: 'Lateral · Left',   hint: 'Left side to camera, arms relaxed.' },
  { key: 'lateral_r', label: 'Lateral · Right',  hint: 'Right side to camera, arms relaxed.' },
  { key: 'sagittal',  label: 'Sagittal',         hint: 'Lateral side view for spinal curve assessment.' },
];

export default function PostureCapture() {
  const [params] = useSearchParams();
  const initialPatient = params.get('patient_id') || '';
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | running | error
  const [err, setErr] = useState('');
  const [metrics, setMetrics] = useState({ angles: {}, posture: '—' });
  const [view, setView] = useState('anterior');
  const [patientId, setPatientId] = useState(initialPatient);
  const [patientList, setPatientList] = useState([]);
  const [bodyPart, setBodyPart] = useState('Full body');

  useEffect(() => {
    api.get('/patients').then(r => setPatientList(r.data || [])).catch(() => {});
  }, []);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedSnapshots, setSavedSnapshots] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // ordered list — drives PDF page order
  const [draggingId, setDraggingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';
  const refreshSaved = async () => {
    if (!patientId) { setSavedSnapshots([]); setSelectedIds([]); return; }
    try {
      const { data } = await api.get(`/posture/${patientId}/snapshots`);
      setSavedSnapshots(data || []);
      // Drop selections that no longer exist after refresh.
      setSelectedIds((prev) => {
        const ids = new Set((data || []).map((s) => s.snapshot_id));
        return prev.filter((id) => ids.has(id));
      });
    } catch (_e) { /* swallow */ }
  };
  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id)
      ? prev.filter((x) => x !== id)
      : [...prev, id]));
  };
  // Iter 84 — drag-handle reordering. Drag a selected card onto another to
  // re-arrange the export order (the order the GP sees in the PDF).
  const onDragStart = (id) => () => {
    if (!selectedIds.includes(id)) return;
    setDraggingId(id);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) { setDraggingId(null); return; }
    setSelectedIds((prev) => {
      const next = prev.filter((x) => x !== draggingId);
      const idx = next.indexOf(targetId);
      if (idx < 0) return [...next, draggingId];
      next.splice(idx, 0, draggingId);
      return next;
    });
    setDraggingId(null);
  };
  const exportPdf = async () => {
    if (!patientId) { toast.error('Pick a patient first'); return; }
    if (selectedIds.length === 0) {
      toast.error(t(locale, 'no_snapshots_to_export'));
      return;
    }
    setExporting(true);
    try {
      const { data } = await api.post(`/posture/${patientId}/pdf`,
        { snapshot_ids: selectedIds },
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `posture-${patientId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('PDF exported.');
    } catch (e) {
      toast.error('Export failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => { refreshSaved(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [patientId]);

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      videoRef.current.srcObject = null;
    }
    setStatus('idle');
  };

  const _composeSnapshot = () => {
    const video = videoRef.current;
    const overlay = canvasRef.current;
    if (!video || !overlay) return null;
    const out = document.createElement('canvas');
    out.width = video.videoWidth || 640;
    out.height = video.videoHeight || 480;
    const ctx = out.getContext('2d');
    ctx.drawImage(video, 0, 0, out.width, out.height);
    ctx.drawImage(overlay, 0, 0, out.width, out.height);
    return out;
  };

  const snapshot = () => {
    try {
      const out = _composeSnapshot();
      if (!out) return;
      out.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const tag = view === 'anterior' ? 'AP' : view === 'posterior' ? 'PA' : view.toUpperCase();
        a.download = `posture-${tag}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        toast.success('Snapshot saved.');
      }, 'image/png');
    } catch (e) {
      toast.error('Snapshot failed: ' + e.message);
    }
  };

  const saveToChart = async () => {
    if (!patientId) {
      toast.error('Pick a patient first');
      return;
    }
    setSaving(true);
    try {
      const out = _composeSnapshot();
      if (!out) throw new Error('No frame to capture');
      const blob = await new Promise((res) => out.toBlob(res, 'image/png'));
      if (!blob) throw new Error('Encoding failed');
      const form = new FormData();
      form.append('file', blob, 'posture.png');
      form.append('view', view);
      form.append('note', note ? `[${bodyPart}] ${note}` : `[${bodyPart}]`);
      form.append('metrics', JSON.stringify(metrics));
      await api.post(`/posture/${patientId}/snapshots`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Saved to patient chart.');
      setNote('');
      refreshSaved();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => stop, []); // cleanup on unmount

  const start = async () => {
    setErr(''); setStatus('loading');
    try {
      // 1. Lazy-load MediaPipe Vision Tasks (only when user clicks Start).
      // eslint-disable-next-line no-undef
      const vision = await import(/* webpackIgnore: true */ MEDIAPIPE_VISION_URL);
      const fileset = await vision.FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm'
      );
      landmarkerRef.current = await vision.PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: 'GPU' },
        runningMode: 'VIDEO',
        numPoses: 1,
      });

      // 2. Camera permission + live stream.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
        audio: false,
      });
      const video = videoRef.current;
      video.srcObject = stream;
      await new Promise((resolve) => {
        video.onloadedmetadata = () => { video.play(); resolve(); };
      });

      setStatus('running');
      // 3. Detect loop.
      const ctx = canvasRef.current.getContext('2d');
      const detect = () => {
        if (!videoRef.current || !landmarkerRef.current) return;
        const now = performance.now();
        const result = landmarkerRef.current.detectForVideo(video, now);
        // Match canvas to current video frame size.
        canvasRef.current.width = video.videoWidth || 640;
        canvasRef.current.height = video.videoHeight || 480;
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#FFB000';
        ctx.fillStyle = '#003D80';
        const newMetrics = { angles: {}, posture: 'good', findings: [] };
        if (result.landmarks && result.landmarks.length > 0) {
          const pts = result.landmarks[0];
          // Draw landmarks
          pts.forEach((p) => {
            const x = p.x * canvasRef.current.width;
            const y = p.y * canvasRef.current.height;
            ctx.beginPath();
            ctx.arc(x, y, 4, 0, 2 * Math.PI);
            ctx.fill();
          });
          // Draw a few key connections
          const segs = [
            [LM.L_SHOULDER, LM.R_SHOULDER],
            [LM.L_SHOULDER, LM.L_HIP], [LM.R_SHOULDER, LM.R_HIP],
            [LM.L_HIP, LM.L_KNEE], [LM.R_HIP, LM.R_KNEE],
            [LM.L_SHOULDER, LM.L_ELBOW], [LM.R_SHOULDER, LM.R_ELBOW],
          ];
          segs.forEach(([a, b]) => {
            ctx.beginPath();
            ctx.moveTo(pts[a].x * canvasRef.current.width, pts[a].y * canvasRef.current.height);
            ctx.lineTo(pts[b].x * canvasRef.current.width, pts[b].y * canvasRef.current.height);
            ctx.stroke();
          });
          // Joint angles
          ANGLES.forEach(([name, a, b, c]) => {
            const angle = jointAngle(pts[a], pts[b], pts[c]);
            newMetrics.angles[name] = Math.round(angle);
          });
          // Posture classifier — Good / Fair / Needs improvement with
          // "what it is vs what it should be" per metric (iter 144).
          const shoulderTilt = Math.abs(pts[LM.L_SHOULDER].y - pts[LM.R_SHOULDER].y) * canvasRef.current.height;
          const headForward = Math.abs(pts[LM.NOSE].x - (pts[LM.L_SHOULDER].x + pts[LM.R_SHOULDER].x) / 2);
          const findings = [];
          if (shoulderTilt > 15) {
            findings.push({
              label: 'Shoulder level difference',
              actual: `${Math.round(shoulderTilt)} px`,
              ideal: '≤ 15 px (level shoulders)',
              severity: shoulderTilt > 30 ? 2 : 1,
            });
          }
          if (headForward > 0.04) {
            findings.push({
              label: 'Forward head offset',
              actual: `${(headForward * 100).toFixed(1)} % of frame`,
              ideal: '≤ 4 % (head stacked over shoulders)',
              severity: headForward > 0.06 ? 2 : 1,
            });
          }
          newMetrics.findings = findings;
          const worst = Math.max(0, ...findings.map(f => f.severity));
          newMetrics.posture = worst >= 2 ? 'needs-improvement' : worst === 1 ? 'fair' : 'good';
        }
        setMetrics(newMetrics);
        rafRef.current = requestAnimationFrame(detect);
      };
      rafRef.current = requestAnimationFrame(detect);
    } catch (e) {
      setStatus('error');
      const msg = e?.message || 'Failed to load MediaPipe Pose.';
      setErr(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">MOTION TRACKING · BETA</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="posture-title">
          Live posture capture
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          Joint-angle overlay + simple posture-class warnings, computed locally on the
          clinician's device via MediaPipe Pose. Nothing leaves the browser unless you
          choose to save it.
        </p>
      </div>

      <div className="bento-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-brand"/> Live feed
          </h3>
          {status === 'idle' && (
            <button onClick={start} className="btn-primary inline-flex items-center gap-2" data-testid="posture-start">
              <Camera className="w-4 h-4"/> Start posture capture
            </button>
          )}
          {status === 'loading' && (
            <div className="text-xs text-fg-muted inline-flex items-center gap-2" data-testid="posture-loading">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading MediaPipe model (one-time, ~5 MB)…
            </div>
          )}
          {status === 'running' && (
            <>
              <button onClick={snapshot} className="btn-secondary inline-flex items-center gap-2" data-testid="posture-snapshot" title="Download annotated frame">
                <Download className="w-4 h-4"/> Download
              </button>
              <button onClick={saveToChart} disabled={!patientId || saving}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="posture-save-to-chart" title="Save annotated frame to the patient chart">
                {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save to chart
              </button>
              <button onClick={stop} className="btn-secondary inline-flex items-center gap-2" data-testid="posture-stop">
                <XCircle className="w-4 h-4"/> Stop
              </button>
            </>
          )}
        </div>

        {/* View selector + patient binding — iter 81 */}
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <label className="block md:col-span-2">
            <div className="text-xs text-fg-muted mb-1">View</div>
            <div className="flex gap-1 flex-wrap" data-testid="posture-view-tabs">
              {VIEWS.map((v) => (
                <button key={v.key}
                        onClick={() => setView(v.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${view === v.key ? 'bg-brand text-white border-brand' : 'bg-bg-secondary/40 text-fg-muted border-soft hover:bg-bg-secondary/70'}`}
                        data-testid={`posture-view-${v.key}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-fg-muted mt-2">
              {VIEWS.find((v) => v.key === view)?.hint}
            </p>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1 flex items-center gap-1">
              <User className="w-3.5 h-3.5"/> Patient
            </div>
            <select className="input" value={patientId}
                    onChange={(e) => setPatientId(e.target.value)}
                    data-testid="posture-patient-id">
              <option value="">— Select patient —</option>
              {patientList.map(p => (
                <option key={p.user_id} value={p.user_id}>{p.full_name || p.user_id}</option>
              ))}
            </select>
            <p className="text-[11px] text-fg-muted mt-1">
              Required to "Save to chart". Auto-selected when opened from a patient chart.
            </p>
          </label>
        </div>

        {/* Iter 144 — which body part is being analysed (dropdown under the camera controls). */}
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Body part being analysed</div>
            <select className="input" value={bodyPart} onChange={(e) => setBodyPart(e.target.value)}
                    data-testid="posture-body-part">
              {BODY_PARTS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
        </div>

        <label className="block mb-4">
          <div className="text-xs text-fg-muted mb-1">Note (optional)</div>
          <input className="input" placeholder="e.g. Day 0 baseline · forward head ≈ 2 cm"
                 value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid="posture-note"/>
        </label>

        {err && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-[#FFF5F5] border border-[#B71C1C]/30 text-[#B71C1C] text-sm mb-3" data-testid="posture-err">
            <AlertCircle className="w-4 h-4"/> {err}
          </div>
        )}

        <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
          <video ref={videoRef} className="w-full h-full object-cover" muted playsInline data-testid="posture-video"/>
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none"/>
          {status === 'idle' && (
            <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
              Camera off
            </div>
          )}
        </div>

        {status === 'running' && (
          <div className="grid md:grid-cols-4 gap-3 mt-4" data-testid="posture-metrics">
            {Object.entries(metrics.angles).map(([name, deg]) => (
              <div key={name} className="rounded-md border border-soft p-3 bg-bg-secondary/40">
                <div className="text-[10px] uppercase tracking-wider text-fg-muted">{name}</div>
                <div className="text-2xl font-bold font-mono text-brand">{deg}°</div>
              </div>
            ))}
            <div className={`rounded-md border p-3 md:col-span-2 ${metrics.posture === 'good' ? 'border-[#1B5E20]/40 bg-[#E8F5E9]' : metrics.posture === 'fair' ? 'border-[#FFB000]/50 bg-[#FFF9EC]' : 'border-[#B71C1C]/40 bg-[#FFF5F5]'}`}
                 data-testid="posture-status-card">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">Posture · {bodyPart}</div>
              <div className="text-sm font-bold flex items-center gap-1.5 mt-1">
                {metrics.posture === 'good' && <><CheckCircle2 className="w-4 h-4 text-[#1B5E20]"/> Good</>}
                {metrics.posture === 'fair' && <><AlertCircle className="w-4 h-4 text-[#A86F31]"/> Fair</>}
                {metrics.posture === 'needs-improvement' && <><AlertCircle className="w-4 h-4 text-[#B71C1C]"/> Needs improvement</>}
              </div>
              {(metrics.findings || []).map((f, i) => (
                <div key={i} className="text-[11px] mt-1.5 leading-snug" data-testid={`posture-finding-${i}`}>
                  <strong>{f.label}:</strong> {f.actual}
                  <span className="text-fg-muted"> · should be {f.ideal}</span>
                </div>
              ))}
              {(metrics.findings || []).length === 0 && (
                <div className="text-[11px] text-fg-muted mt-1">Shoulders level, head stacked over shoulders — within ideal ranges.</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-fg-muted">
        Privacy: this feature runs MediaPipe entirely client-side. The video stream
        never leaves the device unless you explicitly save a snapshot.
      </div>

      {/* Previously-saved snapshots for this patient — iter 81 */}
      {patientId && (
        <div className="bento-card mt-6" data-testid="posture-gallery">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-brand"/> Saved to chart ({savedSnapshots.length})
            </h3>
            {savedSnapshots.length > 0 && (
              <button onClick={exportPdf} disabled={exporting || selectedIds.length === 0}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="posture-export-pdf">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin"/> : <FileDown className="w-4 h-4"/>}
                {t(locale, 'export_to_pdf')} {selectedIds.length > 0 && `(${selectedIds.length})`}
              </button>
            )}
          </div>
          {selectedIds.length > 1 && (
            <p className="text-[11px] text-fg-muted -mt-1 mb-3 flex items-center gap-1.5" data-testid="posture-reorder-hint">
              <GripVertical className="w-3.5 h-3.5"/> Tip: drag the selected cards to re-order the PDF page sequence.
            </p>
          )}
          {savedSnapshots.length === 0 ? (
            <p className="text-xs text-fg-muted">
              No snapshots saved for this patient yet. Capture a view, then tap
              <strong> Save to chart</strong>.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {savedSnapshots.map((s) => {
                const ordinal = selectedIds.indexOf(s.snapshot_id);
                const checked = ordinal >= 0;
                const isDragging = draggingId === s.snapshot_id;
                return (
                  <div key={s.snapshot_id}
                       draggable={checked}
                       onDragStart={onDragStart(s.snapshot_id)}
                       onDragOver={onDragOver}
                       onDrop={onDrop(s.snapshot_id)}
                       className={`relative border rounded-md overflow-hidden bg-bg-secondary/40 cursor-pointer transition-all ${checked ? 'border-brand ring-2 ring-brand/40' : 'border-soft'} ${isDragging ? 'opacity-50 scale-95' : ''}`}
                       onClick={() => toggleSelected(s.snapshot_id)}
                       data-testid={`posture-saved-${s.snapshot_id}`}>
                    <input type="checkbox" checked={checked}
                           onChange={() => toggleSelected(s.snapshot_id)}
                           onClick={(e) => e.stopPropagation()}
                           className="absolute top-2 left-2 z-10 w-4 h-4 accent-brand"
                           data-testid={`posture-select-${s.snapshot_id}`}/>
                    {checked && (
                      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                        <span className="bg-brand text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                              data-testid={`posture-ordinal-${s.snapshot_id}`}>
                          {ordinal + 1}
                        </span>
                        <GripVertical className="w-3.5 h-3.5 text-white drop-shadow"
                                       data-testid={`posture-drag-${s.snapshot_id}`}/>
                      </div>
                    )}
                    <img alt={`${s.view_label} snapshot`}
                         src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/posture/${patientId}/snapshots/${s.snapshot_id}`}
                         className="w-full aspect-video object-cover bg-black pointer-events-none"
                         loading="lazy"/>
                    <div className="p-2 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{s.view_label}</span>
                        <span className="text-fg-muted">{new Date(s.captured_at).toLocaleString()}</span>
                      </div>
                      {s.note && <div className="text-fg-muted mt-1 line-clamp-2">{s.note}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- math helper ----
function jointAngle(a, b, c) {
  // Returns the angle at B in degrees (0–180).
  const v1 = { x: a.x - b.x, y: a.y - b.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const mag1 = Math.hypot(v1.x, v1.y);
  const mag2 = Math.hypot(v2.x, v2.y);
  const cos = Math.max(-1, Math.min(1, dot / (mag1 * mag2 || 1)));
  return (Math.acos(cos) * 180) / Math.PI;
}
